'use client';

import { cloneElement, forwardRef, useContext, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import type { TagPropsInterface } from '../tag';
import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { ProgressStepperStepLabelText } from './progress-stepper-step-label-text';
import { ProgressStepperStepMetaText } from './progress-stepper-step-meta-text';
import { progressStepperStepLabelRecipe } from './styles/progress-stepper-step-label.styles';
import type { ProgressStepperStepLabelPropsInterface } from './types/progress-stepper-step-label.types';

/**
 * Used by ProgressStepperStep to wrap a step's label text and any additional meta content.
 *
 * @since 8.57.0
 */
export const ProgressStepperStepLabel = forwardRef<
  HTMLSpanElement,
  ProgressStepperStepLabelPropsInterface
>((props, forwardedRef) => {
  const { label, className, css: cssProp, meta, ...rest } = props;
  const { alignLabels, labelAppearance, orientation } = useContext(ProgressStepperRootContext);

  const labelAppearanceVariants = splitResponsiveVariant('labelAppearance', labelAppearance);

  // Picks first Tag and enforces `sm` size
  const childTag = useChildrenOfType(meta, 'Tag').at(0);

  const metaContent = useMemo(() => {
    if (typeof meta === 'string') {
      return <ProgressStepperStepMetaText>{meta}</ProgressStepperStepMetaText>;
    }

    if (childTag) {
      return cloneElement<TagPropsInterface>(childTag, { size: 'sm' });
    }

    return null;
  }, [childTag, meta]);

  const styles = css(
    progressStepperStepLabelRecipe.raw({
      alignLabel: alignLabels,
      orientation,
      ...labelAppearanceVariants,
    }),
    cssProp,
  );

  return (
    <span
      className={cx('c11n', styles, className)}
      data-c11n-component="ProgressStepper.StepLabel"
      data-c11n-version={__C11N_VERSION__}
      ref={forwardedRef}
      {...rest}
    >
      <ProgressStepperStepLabelText>{label}</ProgressStepperStepLabelText>
      {metaContent}
    </span>
  );
});

ProgressStepperStepLabel.displayName = 'ProgressStepper.StepLabel';
